import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { Color } from '@/constants/color';
import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import { useCreateAppointment } from '@/hooks/useAppointments';
import useNavigateToPage from '@/hooks/useNavigateToPage';
import AppointmentsRepository from '@/repositories/AppointmentsRepository';
import Alert from '@/ui/components/Alert';
import Badge from '@/ui/components/Badge';
import Button from '@/ui/components/Button';
import Card from '@/ui/components/Card';
import Heading from '@/ui/components/Heading';
import Spinner from '@/ui/components/Spinner';
import { Tooltip } from '@/ui/Tooltip';
import { parseHealthieDate } from '@/utils/dates';

import PersonContext from '../Person/PersonContext';

import { constraintPillLabel, dayDiffFromTarget, meetsConstraint } from './bookingConstraints';
import ConfirmationModal from './ConfirmationModal';
import {
  AvailableSlotsResponse,
  BaseSlotsProps,
  EnrichedDaySlot,
  EnrichedSlotResponse,
  EnrichedTimeSlot,
  StaffOption
} from './types';

/**
 * ProxySlots - New implementation using the backend proxy endpoint that returns
 * pre-enriched slots with status information (availability, conflicts, etc.).
 */
export const ProxySlots = (props: BaseSlotsProps) => {
  const { navigateToAppointments, navigateToAppointment } = useNavigateToPage();
  const { addMessage } = useContext(AppContext);
  const [searchParams] = useSearchParams();
  const selectedSlotParam = searchParams.get('selectedSlot');
  const selectedSlotTime = selectedSlotParam ? new Date(selectedSlotParam).getTime() : null;
  const selectedSlotRef = useRef<HTMLDivElement | null>(null);

  const {
    person: { id: personId },
    refetch: refetchPerson
  } = useContext(PersonContext);
  const appointmentsRepo = AppointmentsRepository();
  const [endDate, setEndDate] = useState<Date>(props.endDate);
  const [loadedData, setLoadedData] = useState<EnrichedDaySlot[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [selectedSlot, setSelectedSlot] = useState<EnrichedTimeSlot | null>(null);
  const showExtraSlots = props.showExtraSlots ?? false;

  const { mutate: createAppointment, isPending: createPending } = useCreateAppointment({
    onSuccess: () => {
      addMessage('success', 'Appointment created successfully.');
      setLoadedData([]);
      setSelectedSlot(null);
      // set_primary_provider may have changed the person's staff relationships,
      // which PersonContext otherwise wouldn't refresh on its own.
      refetchPerson();
      navigateToAppointments(personId);
    },
    onError: (error) => {
      // The backend returns actionable detail in the response body (e.g. a
      // Healthie account is still being created for this person), which axios
      // does not surface on `error.message`. Prefer the server message so the
      // user learns why booking failed and whether to retry.
      const serverMessage = (error.response?.data as { error?: string } | undefined)?.error;
      addMessage('error', `Error creating appointment: ${serverMessage ?? error.message}`);
      setModalOpen(false);
      setSelectedSlot(null);
    }
  });

  const queryParams = useMemo(() => {
    if (props.providers.length === 0 || !props.appointmentType) {
      return null;
    }

    return {
      staff_ids: props.providers.map((prov) => prov.id),
      appointment_type_id: props.appointmentType.id,
      start_on: props.startDate.toISOString(),
      end_on: endDate.toISOString(),
      timezone: props.timeZone,
      appointment_duration_minutes: props.appointmentDurationMinutes,
      jurisdiction_id: props.jurisdictionId,
      person_id: personId
    };
  }, [
    props.providers,
    props.appointmentType,
    props.startDate,
    endDate,
    props.timeZone,
    props.appointmentDurationMinutes,
    props.jurisdictionId,
    personId
  ]);

  const {
    data: slotsData,
    refetch,
    isPending: slotsLoading
  } = useAPI<AvailableSlotsResponse>({
    enabled: Boolean(queryParams),
    queryKey: [
      'availableSlots',
      queryParams?.staff_ids,
      queryParams?.appointment_type_id,
      queryParams?.start_on,
      queryParams?.end_on,
      queryParams?.timezone,
      queryParams?.appointment_duration_minutes,
      queryParams?.jurisdiction_id,
      queryParams?.person_id
    ],
    queryFn: async () => {
      if (!queryParams) {
        throw new Error('Missing query parameters');
      }

      const response = await appointmentsRepo.getAvailableSlots(queryParams);

      if (!response.data || response.isError) {
        throw new Error(response.errorMessage || 'Failed to load available slots');
      }

      return response.data;
    }
  });

  useEffect(() => {
    setEndDate(props.endDate);
  }, [props.endDate]);

  useEffect(() => {
    if (selectedSlotParam && selectedSlotRef.current && loadedData.length > 0) {
      selectedSlotRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [loadedData, selectedSlotParam]);

  useEffect(() => {
    if (!slotsData?.slots) {
      return;
    }

    const slotsByDate = new Map<
      string,
      Map<string, { providers: Set<string>; enrichedSlot: EnrichedSlotResponse }>
    >();

    slotsData.slots.forEach((slot) => {
      if (!slot.date || !slot.user_id) return;

      const parsedDate = parseHealthieDate(slot.date);
      if (!parsedDate) return;

      const dateKey = slot.date.split(' ')[0];
      const timeKey = slot.date;

      if (!slotsByDate.has(dateKey)) {
        slotsByDate.set(dateKey, new Map());
      }

      const dateGroup = slotsByDate.get(dateKey)!;
      if (!dateGroup.has(timeKey)) {
        dateGroup.set(timeKey, { providers: new Set(), enrichedSlot: slot });
      }

      dateGroup.get(timeKey)!.providers.add(slot.user_id);
    });

    const allDates: string[] = [];
    const currentDate = new Date(props.startDate);
    const finalDate = new Date(endDate);

    while (currentDate <= finalDate) {
      const dateKey = currentDate.toISOString().split('T')[0];
      allDates.push(dateKey);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    const result: EnrichedDaySlot[] = allDates.map((date) => {
      const timeSlotsMap = slotsByDate.get(date);

      if (!timeSlotsMap || timeSlotsMap.size === 0) {
        return {
          date,
          slots: []
        };
      }

      const slots = Array.from(timeSlotsMap.entries()).map(
        ([time, { providers: providerIdsSet, enrichedSlot }]) => {
          const providerIds = Array.from(providerIdsSet);
          const matchedProviders = props.providers.filter((provider) =>
            providerIds.includes(String(provider.healthie_id))
          );

          return {
            time,
            providers: matchedProviders,
            status: enrichedSlot.status,
            reason: enrichedSlot.reason,
            overlappingAppointmentId: enrichedSlot.overlapping_appointment?.id,
            overlappingAppointmentTitle: enrichedSlot.overlapping_appointment?.title
          };
        }
      );

      slots.sort((a, b) => a.time.localeCompare(b.time));

      return {
        date,
        slots
      };
    });

    setLoadedData(result);
  }, [slotsData?.slots, props.providers, props.startDate, endDate]);

  const getDefaultProviderId = (providers: StaffOption[]): number => {
    if (!providers || providers.length === 0) {
      return 0;
    }

    if (providers.length === 1) {
      return providers[0].healthie_id;
    }

    const mostRecent = providers.find((provider) => provider.is_most_recent);
    if (mostRecent) {
      return mostRecent.healthie_id;
    }

    const providerSlotCounts = new Map<number, number>();

    loadedData.forEach((daySlot) => {
      daySlot.slots.forEach((slot) => {
        slot.providers.forEach((provider) => {
          const count = providerSlotCounts.get(provider.healthie_id) || 0;
          providerSlotCounts.set(provider.healthie_id, count + 1);
        });
      });
    });

    let maxSlots = 0;
    let defaultProviderId = providers[0]?.healthie_id || 0;

    providers.forEach((provider) => {
      const slotCount = providerSlotCounts.get(provider.healthie_id) || 0;
      if (slotCount > maxSlots) {
        maxSlots = slotCount;
        defaultProviderId = provider.healthie_id;
      }
    });

    return defaultProviderId;
  };

  const getSlotColor = (slot: EnrichedTimeSlot): Color => {
    switch (slot.status) {
      case 'overlapping_appointment':
        return Color.ORANGE;
      case 'same_day_restricted':
        return Color.RED;
      case 'person_unavailable':
      case 'blocked_time':
      case 'staff_conflict_of_interest':
      case 'person_conflict_of_interest':
        return Color.WHITE;
      case 'available':
      default:
        return Color.GREEN;
    }
  };

  const getSlotTooltip = (slot: EnrichedTimeSlot): string | undefined => {
    switch (slot.status) {
      case 'overlapping_appointment': {
        const title = slot.overlappingAppointmentTitle || 'Appointment';
        return `Overlaps with: ${title}. Click to view appointment.`;
      }
      case 'person_unavailable':
        return "This time slot is outside the member's availability.";
      case 'staff_conflict_of_interest':
        return slot.reason || 'Staff has a conflict of interest for this time slot.';
      case 'person_conflict_of_interest':
        return slot.reason || 'Person has a conflict of interest for this time slot.';
      case 'same_day_restricted':
        return slot.reason || 'Same-day appointment restriction applies';
      case 'blocked_time':
        return slot.reason || 'This time slot is blocked.';
      default:
        return undefined;
    }
  };

  if (!props.appointmentType) {
    return (
      <Alert level="info" dismissable={false}>
        Please select an appointment type to view available time slots.
      </Alert>
    );
  }

  if (!props.providers || props.providers.length === 0) {
    return (
      <Alert level="info" dismissable={false}>
        Please select at least one provider to view available time slots.
      </Alert>
    );
  }

  if (slotsLoading && loadedData.length === 0) {
    return (
      <div className="flex justify-center items-center my-4">
        <Spinner label="Loading available time slots..." />
      </div>
    );
  }

  if (loadedData.length === 0 && !slotsLoading) {
    return (
      <Alert level="warn" dismissable={false}>
        No available time slots for the selected providers
      </Alert>
    );
  }

  const { constraints } = props;
  const preferredDays = constraints
    ? loadedData.filter((d) => meetsConstraint(d.date, constraints))
    : loadedData;

  const renderDaySlot = (daySlot: EnrichedDaySlot) => {
    const constraintPill = constraints
      ? (() => {
          const diff = dayDiffFromTarget(daySlot.date, constraints.targetDate);
          const inRange = meetsConstraint(daySlot.date, constraints);
          return (
            <Badge color={inRange ? Color.GREEN : Color.AMBER} className="ml-2 shrink-0">
              {constraintPillLabel(diff)}
            </Badge>
          );
        })()
      : null;

    return daySlot.slots.length > 0 ? (
      <Card
        key={daySlot.date}
        className="p-2"
        topBar={{
          content: (
            <div className="flex flex-row items-center">
              <Heading value={3}>
                {new Date(daySlot.date + 'T12:00:00').toLocaleDateString('en-US', {
                  weekday: 'long',
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  timeZone: props.timeZone
                })}
              </Heading>
              {constraintPill}
              <Heading
                value={6}
                className="ml-auto">{`${daySlot.slots.length} slots available`}</Heading>
            </div>
          )
        }}>
        <div className="grid grid-cols-2 gap-2">
          {slotsLoading ? (
            <Spinner label="Loading available time slots..." />
          ) : (
            daySlot.slots.map((slot) => {
              const buttonColor = getSlotColor(slot);
              const tooltipContent = getSlotTooltip(slot);
              const isSelectedSlot =
                selectedSlotTime !== null && new Date(slot.time).getTime() === selectedSlotTime;

              const formattedTime = new Date(slot.time).toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit',
                timeZone: props.timeZone
              });

              return (
                <div
                  key={slot.time}
                  ref={isSelectedSlot ? selectedSlotRef : undefined}
                  className={
                    isSelectedSlot ? 'ring-2 ring-blue-500 ring-offset-1 rounded' : undefined
                  }>
                  <Tooltip
                    content={tooltipContent}
                    disabled={!tooltipContent}
                    triggerClassName="block w-full">
                    <Button
                      color={buttonColor}
                      title={tooltipContent}
                      className="w-full"
                      onClick={() => {
                        if (
                          slot.status === 'overlapping_appointment' &&
                          slot.overlappingAppointmentId
                        ) {
                          navigateToAppointment(personId, slot.overlappingAppointmentId);
                        } else {
                          setSelectedSlot(slot);
                          setModalOpen(true);
                        }
                      }}>
                      {formattedTime}
                    </Button>
                  </Tooltip>
                </div>
              );
            })
          )}
        </div>
      </Card>
    ) : (
      <div
        className="group/section rounded-lg shadow mb-2 h-full relative bg-white p-5 flex flex-row items-center"
        key={daySlot.date}>
        <Heading value={3}>
          {new Date(daySlot.date + 'T12:00:00').toLocaleDateString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            timeZone: props.timeZone
          })}
        </Heading>
        {constraintPill}
        <Heading value={6} className="ml-auto">{`${daySlot.slots.length} slots available`}</Heading>
      </div>
    );
  };

  return (
    <div>
      {showExtraSlots ? loadedData.map(renderDaySlot) : preferredDays.map(renderDaySlot)}

      <Button
        className="w-full"
        size="xs"
        color={Color.GREEN}
        loading={slotsLoading}
        disabled={slotsLoading || !props.allowLoadMore}
        onClick={() => {
          const newDate = new Date(endDate);
          newDate.setDate(newDate.getDate() + 7);
          setEndDate(newDate);
        }}>
        Load next 7 days
      </Button>

      {selectedSlot && (
        <ConfirmationModal
          open={modalOpen}
          slot={selectedSlot}
          appointmentType={props.appointmentType}
          timeZone={props.timeZone}
          defaultProviderId={getDefaultProviderId(selectedSlot.providers)}
          onClose={() => {
            setModalOpen(false);
            setSelectedSlot(null);
            refetch();
          }}
          isPending={createPending}
          onSubmit={(variables) => {
            createAppointment({ ...variables, person_id: personId });
          }}
        />
      )}
    </div>
  );
};
